import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Users } from "lucide-react";
import { Program } from "../../types/program";
import { replaceHarvardWithIvyLeague, removeCurrencySymbol } from "../../utils/utils";
import CurrencyIcon from "./CurrencyIcon";

export type HeroTheme = "blue" | "green" | "purple" | "teal" | "orange";

const themeColors: Record<HeroTheme, { gradient: string; link: string; badge: string; desc: string; clock: string }> = {
  blue: {
    gradient: "from-blue-900 via-blue-800 to-purple-900",
    link: "text-blue-200",
    badge: "bg-blue-600/80",
    desc: "text-blue-100",
    clock: "text-blue-300",
  },
  green: {
    gradient: "from-green-900 via-green-800 to-emerald-900",
    link: "text-green-200",
    badge: "bg-green-600/80",
    desc: "text-green-100",
    clock: "text-green-300",
  },
  purple: {
    gradient: "from-purple-900 via-purple-800 to-pink-900",
    link: "text-purple-200",
    badge: "bg-purple-600/80",
    desc: "text-purple-100",
    clock: "text-purple-300",
  },
  teal: {
    gradient: "from-teal-900 via-teal-800 to-cyan-900",
    link: "text-teal-200",
    badge: "bg-teal-600/80",
    desc: "text-teal-100",
    clock: "text-teal-300",
  },
  orange: {
    gradient: "from-orange-900 via-orange-800 to-red-900",
    link: "text-orange-200",
    badge: "bg-orange-600/80",
    desc: "text-orange-100",
    clock: "text-orange-300",
  },
};

interface HeroSectionProps {
  program: Program;
  theme?: HeroTheme;
}

const HeroSection: React.FC<HeroSectionProps> = ({ program, theme = "blue" }) => {
  const colors = themeColors[theme];

  return (
    <div className={`relative bg-gradient-to-br ${colors.gradient} text-white overflow-hidden`}>
      <div className="absolute inset-0 bg-black/20"></div>
      {program.image && (
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${program.image})` }}
        ></div>
      )}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link
            to="/programs"
            className={`inline-flex items-center ${colors.link} hover:text-white mb-6 transition-colors`}
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Programs
          </Link>

          <div className="mb-4">
            <span className={`inline-block px-4 py-2 ${colors.badge} backdrop-blur-sm rounded-full text-sm font-semibold uppercase tracking-wider`}>
              {program.category}
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            {replaceHarvardWithIvyLeague(program.title)}
          </h1>

          <p className={`text-xl ${colors.desc} mb-8 max-w-3xl leading-relaxed`}>
            {replaceHarvardWithIvyLeague(program.description)}
          </p>

          <div className="flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center">
              <Clock className={`h-5 w-5 mr-2 ${colors.clock}`} />
              <span>{program.duration}</span>
            </div>
            {program.is_active !== false && (
              <div className="flex items-center">
                <CurrencyIcon currency={program.currency} className="h-5 w-5 mr-2 text-green-300" />
                <span className="text-2xl font-bold">
                  {removeCurrencySymbol(program.formatted_price)}
                </span>
              </div>
            )}
            {program.available_tickets && (
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2 text-yellow-300" />
                <span>{program.remaining_tickets} spots available</span>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HeroSection;
